import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { PromotedEventSportState } from 'src/main/cms/PromotedEvents/promotedEventsState/PromotedEventSportState';
import { PromotedEventCompetitionState } from 'src/main/cms/PromotedEvents/promotedEventsState/PromotedEventCompetitionState';
import { PromotedEventState } from 'src/main/cms/PromotedEvents/promotedEventsState/PromotedEventState';
import {
    PromotedEventsState,
    EventCombinedType,
    CompetitionCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { ListItemSecondary } from 'src/domains/layouts/shared/cms/list/listItems/ListItemSecondary';
import { useAppStateContext } from 'src/AppState/AppState';

interface SportSectionPropsType {
    label: string;
    isOpen: boolean;
    isDragged: boolean;
    promotedEventSportState: PromotedEventSportState;
    dataTest?: string;
}

export const PromotedEventSportSection = observer(
    'PromotedEventSportSection',
    ({ label, isOpen, isDragged, promotedEventSportState, dataTest }: SportSectionPropsType) => {
        const {
            setSportActive,
            isActive,
            handleToggleOpenSection,
            handleDeleteSport,
            handleShowDeletePopup,
            handleHideDeletePopup,
            showDeletePopup,
        } = promotedEventSportState;

        return (
            <ListItemSecondary
                label={label}
                isDragged={isDragged}
                setPublished={setSportActive}
                isPublished={isActive}
                handleToggleOpenSection={handleToggleOpenSection}
                isOpenSection={isOpen}
                handleDelete={handleDeleteSport}
                handleDragAndDrop={true}
                hasSubsection={true}
                handleShowDeletePopup={handleShowDeletePopup}
                handleHideDeletePopup={handleHideDeletePopup}
                showDeletePopup={showDeletePopup}
                dataTest={dataTest}
            />
        );
    }
);

interface CompetitionSectionPropsType {
    label: string;
    isOpen: boolean;
    isDragged: boolean;
    promotedEventCompetitionState: PromotedEventCompetitionState;
    dataTest?: string;
}

export const PromotedEventCompetitionSection = observer(
    'PromotedEventCompetitionSection',
    ({ label, isOpen, isDragged, promotedEventCompetitionState, dataTest }: CompetitionSectionPropsType) => {
        const {
            setCompetitionActive,
            isActive,
            handleToggleOpenSection,
            handleDeleteCompetition,
            handleShowDeletePopup,
            handleHideDeletePopup,
            showDeletePopup,
        } = promotedEventCompetitionState;

        return (
            <ListItemSecondary
                label={label}
                setPublished={setCompetitionActive}
                isPublished={isActive}
                isDragged={isDragged}
                handleDragAndDrop={true}
                hasSubsection={true}
                subsection='one'
                handleToggleOpenSection={handleToggleOpenSection}
                isOpenSection={isOpen}
                handleDelete={handleDeleteCompetition}
                handleShowDeletePopup={handleShowDeletePopup}
                handleHideDeletePopup={handleHideDeletePopup}
                showDeletePopup={showDeletePopup}
                dataTest={dataTest}
            />
        );
    }
);

interface EventSectionPropsType {
    eventData: EventCombinedType;
    getCompetitionData: (competitionId: number) => CompetitionCombinedType | null;
    handleDeleteCompetition: (sportSlug: string) => Promise<void>;
    isDragged: boolean;
    promotedEventsSectionState: PromotedEventsState;
    dataTest?: string;
}

export const PromotedEventSection = observer(
    'PromotedEventSection',
    ({
        eventData,
        getCompetitionData,
        handleDeleteCompetition,
        promotedEventsSectionState,
        isDragged,
        dataTest,
    }: EventSectionPropsType) => {
        const { sdkStaff, trpcClient } = useAppStateContext();
        const [promotedEventState] = useState(
            () =>
                new PromotedEventState(
                    trpcClient,
                    sdkStaff,
                    eventData,
                    getCompetitionData,
                    handleDeleteCompetition,
                    promotedEventsSectionState
                )
        );

        const {
            setEventActive,
            isActive,
            handleDeleteEvent,
            handleShowDeletePopup,
            handleHideDeletePopup,
            showDeletePopup,
            isEventFinished,
            eventName,
        } = promotedEventState;

        if (isEventFinished === null || isEventFinished) {
            return null;
        }

        return (
            <ListItemSecondary
                label={eventName}
                subsection='two'
                setPublished={setEventActive}
                handleDragAndDrop={true}
                isPublished={isActive}
                isDragged={isDragged}
                handleDelete={handleDeleteEvent}
                handleShowDeletePopup={handleShowDeletePopup}
                handleHideDeletePopup={handleHideDeletePopup}
                showDeletePopup={showDeletePopup}
                dataTest={dataTest}
            />
        );
    }
);
